import * as React from "react";
import { animate } from "motion/react";
import { useContainerSize } from "~/lib/use-container-size";
import { InteractiveCard } from "~/components/ui/interactive-card";
import { ScrollCarousel, type ScrollCarouselHandle } from "./ScrollCarousel";

const CARD_W_COLLAPSED = 411;
const CARD_W_OVERLAY = 310;
const CARD_GAP = 24;
// Minimum visible strip of the neighbouring card when one is expanded.
const PEEK = 100;
// Minimum nav container width (px) for the side-by-side horizontal expand layout.
// Below this the card opens as a fullscreen overlay instead.
// Corresponds to --container-lg (832px) in design-tokens.css.
const MIN_EXPAND_WIDTH = 832;

export interface CarouselItem {
  id: string;
  title: string;
  description: string;
  cta: string;
  seeAll: string;
  bg: string;
  featureImg?: string;
}

export default function Carousel({ items }: { items: CarouselItem[] }) {
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const scrollRef = React.useRef<ScrollCarouselHandle>(null);
  // Callback ref so useContainerSize sees the element on first mount.
  const [navEl, setNavEl] = React.useState<HTMLDivElement | null>(null);

  // Measure the nav container's actual rendered width. Seed with window.innerWidth so
  // the first render assumes desktop layout and avoids a flash of overlay-sized cards.
  const { width: navWidth } = useContainerSize(navEl, {
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: 0,
  });

  // True when there's room for the side-by-side expand; false → fullscreen overlay.
  const canExpand = navWidth >= MIN_EXPAND_WIDTH;

  // Expanded card fills the nav width minus a peek of the next card.
  const expandedWidth = Math.max(0, navWidth - CARD_GAP - PEEK);

  // Refs for coordinating the two-phase sequence (scroll → expand).
  const scrollCtrlRef = React.useRef<{ stop(): void } | null>(null);
  // Increment each time handleToggle fires; lets onComplete detect a stale callback.
  const triggerRef = React.useRef(0);
  // The scroll target the RAF chase aims for during card expansion.
  const expandScrollTargetRef = React.useRef(0);
  const chaseRafRef = React.useRef<number | null>(null);


  React.useEffect(() => {
    return () => {
      scrollCtrlRef.current?.stop();
      if (chaseRafRef.current !== null) cancelAnimationFrame(chaseRafRef.current);
    };
  }, []);


  // After expansion begins, chase the stored scroll target every frame.
  //
  // For cards well within scroll range (0–2 on a typical screen): the slide already
  // landed at targetLeft, so this is a no-op.
  //
  // For the second-to-last card: the pre-expansion scroll was capped at maxScroll.
  // As the card grows it extends scrollWidth, so maxScroll rises and the browser's
  // clamp lifts — scrollLeft follows naturally, pulling the viewport into position.
  //
  // For the last card (target = 99999): pins the right edge as it expands leftward.
  React.useEffect(() => {
    if (chaseRafRef.current !== null) {
      cancelAnimationFrame(chaseRafRef.current);
      chaseRafRef.current = null;
    }
    if (!expandedId) return;

    const target = expandScrollTargetRef.current;
    const startTime = performance.now();
    const CHASE_MS = 700; // slightly longer than the 550ms width animation
    function chase() {
      const el = scrollRef.current?.scrollEl; if (el) el.scrollLeft = target;
      if (performance.now() - startTime < CHASE_MS) {
        chaseRafRef.current = requestAnimationFrame(chase);
      } else {
        chaseRafRef.current = null;
      }
    }
    chaseRafRef.current = requestAnimationFrame(chase);
    return () => {
      if (chaseRafRef.current !== null) {
        cancelAnimationFrame(chaseRafRef.current);
        chaseRafRef.current = null;
      }
    };
  }, [expandedId]);

  // Two-phase interaction: slide the carousel so the card is at its edge, THEN expand.
  // The Motion animate() call drives scrollLeft directly and calls onComplete when done.
  // triggerRef guards against a stale onComplete firing after a subsequent click.
  function handleToggle(id: string) {
    scrollCtrlRef.current?.stop();
    scrollCtrlRef.current = null;

    // Clicking the open card → collapse, no scroll needed.
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    // Overlay mode: skip scroll animation; overlay handles the expand view.
    // Pin the chase target to the current position so the RAF effect is a no-op.
    if (!canExpand) {
      expandScrollTargetRef.current = scrollRef.current?.scrollEl?.scrollLeft ?? 0;
      setExpandedId(id);
      return;
    }

    const el = scrollRef.current?.scrollEl;
    if (!el) return;

    const idx = items.findIndex(c => c.id === id);
    const isLast = idx === items.length - 1;

    // The scroll position we want once the card is fully expanded.
    // Last card: 99999 pins the right edge (browser clamps to real max, which grows).
    // Others: the card's natural left-edge position in the collapsed layout.
    const expandTarget = isLast ? 99999 : idx * (CARD_W_COLLAPSED + CARD_GAP);
    expandScrollTargetRef.current = expandTarget;

    // The slide phase can only reach the current max scroll (pre-expansion).
    // For near-end cards whose expandTarget exceeds this, the RAF chase picks up
    // the rest as the expanding card pushes scrollWidth higher.
    const preMaxScroll = el.scrollWidth - el.clientWidth;
    const slideTarget = Math.min(expandTarget, preMaxScroll);

    const myTrigger = ++triggerRef.current;

    function doExpand() {
      if (triggerRef.current !== myTrigger) return;
      setExpandedId(id);
    }

    const fromLeft = el.scrollLeft;
    if (Math.abs(fromLeft - slideTarget) <= 1) {
      doExpand();
      return;
    }

    scrollCtrlRef.current = animate(fromLeft, slideTarget, {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
      onUpdate: (v: number) => { const el = scrollRef.current?.scrollEl; if (el) el.scrollLeft = v; },
      onComplete: doExpand,
    });
  }

  const stepCardWidth = canExpand ? CARD_W_COLLAPSED : CARD_W_OVERLAY;

  return (
    <ScrollCarousel
      ref={scrollRef}
      cardWidth={stepCardWidth}
      cardGap={CARD_GAP}
      navRef={setNavEl}
      ariaLabel="Use cases"
    >
      {items.map((item) => (
        <InteractiveCard
          key={item.id}
          title={item.title}
          description={item.description}
          cta={item.cta}
          seeAll={item.seeAll}
          bg={item.bg}
          featureImg={item.featureImg}
          expandedWidth={expandedWidth}
          isExpanded={expandedId === item.id}
          overlay={!canExpand}
          onToggle={() => handleToggle(item.id)}
          itemId={item.id}
        />
      ))}
    </ScrollCarousel>
  );
}
